/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import React, { useState, useEffect } from 'react';
import { trackFooterNavMenu } from '../utils/track';
import { SmartLink as Link } from '@/utils/smartLink';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLabels } from '@/hooks/useLabels';
import NextImage from '@/components/NextImage';
import styles from './FooterLink.module.scss';

type Props = {
  CTALink: string;
  name: string;
  className?: string;
  footerSectionName?: '' | 'top' | 'middle' | 'bottom';
  icon?: string;
};

const labelMap = {
  newWindow: 'label_OpenaNewWindow',
};

export function FooterLink({ CTALink, name, className, footerSectionName = '', icon }: Props) {
  const matchesTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const matchesMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const [shouldFocusTop, setShouldFocusTop] = useState(false);

  const labels = useLabels(labelMap);

  useEffect(() => {
    if (shouldFocusTop) {
      const azLogoFixed = document.getElementById('azLogoLinkFixed');
      const azLogoScroll = document.getElementById('azLogoLinkScroll');
      const hamburgerMenuBtn = document.getElementById('menuNavBtnScroll');
      if (matchesTablet) {
        azLogoScroll?.focus();
      }
      if (matchesMobile) {
        hamburgerMenuBtn?.focus();
      } else {
        azLogoFixed?.focus();
      }
      setShouldFocusTop(false);
    }
  }, [shouldFocusTop, matchesMobile, matchesTablet]);

  const handleOnClick = () => {
    setShouldFocusTop(true);
    trackFooterNavMenu(name);
  };

  return (
    <Link
      to={CTALink}
      onClick={handleOnClick}
      data-testid={`footer_${footerSectionName}_link_${name.replace(/\s/g, '-').toLowerCase()}`}
      className={icon ? `${className} ${styles.linkWithIcon}` : className}
      externalLinkLabel={labels.newWindow}
    >
      {icon && <NextImage src={icon} width={32} height={20} loading="lazy" />}
      {name}
    </Link>
  );
}
